/**
 * Upload progress overlay component for displaying image upload status
 * V3 Design System
 */
import { View, ScrollView, Image, Pressable, ActivityIndicator, StyleSheet } from 'react-native';
import { heightPercentageToDP as hp, widthPercentageToDP as wp } from 'react-native-responsive-screen';
import { Feather } from '@expo/vector-icons';

import CustomText from '../CustomText';
import AppStyles from '../../AppStyles';
import { useTranslation } from '../../i18n';

const UploadStatusIndicator = ({ status }) => {
  switch (status) {
    case 'Picked':
      return <Feather name="arrow-up-circle" size={24} color={AppStyles.colorSet.textSecondary} />;
    case 'Uploading':
      return <ActivityIndicator size="large" color={AppStyles.colorSet.brandPrimary} />;
    case 'Failed':
      return <Feather name="alert-circle" size={24} color={AppStyles.colorSet.error} />;
    case 'Finished':
      return <Feather name="check-circle" size={24} color={AppStyles.colorSet.success} />;
    default:
      return null;
  }
};

const UploadProgressOverlay = ({
  uploadStage,
  uploadProgress,
  isDesktop,
  onCancel,
  onRetry,
  onSubmitWithSuccessful,
  showFailedActions,
}) => {
  const { t } = useTranslation(['reports', 'common']);

  const shouldShow =
    uploadStage &&
    (uploadStage === 'Uploading' || uploadStage === 'Finished' || uploadStage === 'Failed') &&
    uploadProgress &&
    Object.keys(uploadProgress).length > 0;

  if (!shouldShow) return null;

  return (
    <ScrollView
      containerStyle={{
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
      }}
    >
      <View style={styles.container}>
        <View style={isDesktop ? styles.titleDesktop : styles.titleMobile}>
          <CustomText font="medium" size={25} color="#000">
            {t('upload.progressTitle')}
          </CustomText>
        </View>

        {Object.keys(uploadProgress).map((key, index) => (
          <View key={key} style={styles.row}>
            <Image
              style={styles.thumbnail}
              source={{ uri: key }}
            />
            <CustomText
              style={isDesktop ? styles.textDesktop : styles.textMobile}
              font="medium"
              size={20}
              color="#000"
            >
              {t('upload.imageStatus', { index: index + 1, status: uploadProgress[key] })}
            </CustomText>
            <View style={isDesktop ? styles.indicatorDesktop : styles.indicatorMobile}>
              <UploadStatusIndicator status={uploadProgress[key]} />
            </View>
          </View>
        ))}

        {showFailedActions && (
          <View style={styles.actionButtonsContainer}>
            <Pressable onPress={onRetry} style={styles.actionButton}>
              <CustomText style={styles.buttonText} font="medium" size={18} color={AppStyles.colorSet.textPrimary}>
                {t('upload.retryFailed')}
              </CustomText>
            </Pressable>
            <Pressable onPress={onSubmitWithSuccessful} style={styles.actionButtonSecondary}>
              <CustomText style={styles.buttonText} font="medium" size={18} color={AppStyles.colorSet.textPrimary}>
                {t('upload.submitSuccessfulOnly')}
              </CustomText>
            </Pressable>
            <Pressable onPress={onCancel} style={styles.actionButtonSecondary}>
              <CustomText style={styles.buttonText} font="medium" size={18} color={AppStyles.colorSet.textSecondary}>
                {t('common:actions.cancel')}
              </CustomText>
            </Pressable>
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: AppStyles.borderRadii.lg,
    borderColor: AppStyles.colorSet.cardBorder,
    borderWidth: 1,
    minHeight: hp(60),
    maxHeight: hp(80),
    margin: wp(5),
    padding: AppStyles.spacing.xxl,
    backgroundColor: AppStyles.colorSet.cardBackground,
    alignItems: 'center',
    flexDirection: 'column',
    ...AppStyles.styleSet.shadowLarge,
  },
  titleDesktop: {
    flex: 0,
    paddingBottom: AppStyles.spacing.xxl,
    paddingTop: AppStyles.spacing.md,
    marginBottom: AppStyles.spacing.sm,
  },
  titleMobile: {
    flex: 0,
    paddingTop: AppStyles.spacing.md,
    paddingBottom: AppStyles.spacing.xl,
    marginBottom: AppStyles.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: AppStyles.spacing.md,
    paddingHorizontal: AppStyles.spacing.lg,
    marginVertical: AppStyles.spacing.xs,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: AppStyles.borderRadii.md,
    width: '100%',
  },
  thumbnail: {
    height: wp(10),
    width: wp(10),
    maxHeight: 60,
    maxWidth: 60,
    borderRadius: AppStyles.borderRadii.sm,
  },
  textDesktop: {
    marginLeft: AppStyles.spacing.xl,
    paddingHorizontal: AppStyles.spacing.lg,
    flex: 5,
  },
  textMobile: {
    marginLeft: AppStyles.spacing.md,
    flex: 5,
  },
  indicatorDesktop: {
    marginLeft: AppStyles.spacing.xl,
    paddingHorizontal: AppStyles.spacing.lg,
    flex: 1,
    alignItems: 'center',
  },
  indicatorMobile: {
    marginLeft: AppStyles.spacing.md,
    flex: 1,
    alignItems: 'center',
  },
  actionButtonsContainer: {
    marginTop: AppStyles.spacing.xxl,
    width: '100%',
    gap: AppStyles.spacing.md,
  },
  actionButton: {
    cursor: 'pointer',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    borderRadius: AppStyles.borderRadii.md,
    paddingVertical: AppStyles.spacing.lg,
    paddingHorizontal: AppStyles.spacing.xl,
  },
  actionButtonSecondary: {
    cursor: 'pointer',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderRadius: AppStyles.borderRadii.md,
    borderWidth: 2,
    borderColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: AppStyles.spacing.lg - 2,
    paddingHorizontal: AppStyles.spacing.xl,
  },
  buttonText: {
    textAlign: 'center',
  },
});

export default UploadProgressOverlay;
